import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/dist/query/react";
import { skipToken } from "@reduxjs/toolkit/query";
import { addDoc, collection, deleteDoc, doc, getDocs, limit as firestoreLimit, orderBy, query, startAfter, updateDoc, where, QueryConstraint, getDoc, getCountFromServer } from "firebase/firestore";
import { db } from "@/firebase";
import pick from "lodash/pick";
import { RootState } from "@/store/store";
import { addLoadingTask, deleteLoadingTask } from "@/store/uiSlice";
import { fetchUsers } from "@/store/usersSlice";
import { Content, ContentStatus, ContentType, DraftContent, NewOffer, Offer } from "@/types";
import { addOrganizationContent } from "../slice";
import { ApiError } from "@/types/api";

export const contentApi = createApi({
  reducerPath: "content",
  baseQuery: fakeBaseQuery<ApiError>(),
  tagTypes: ["Content", "DisabledContent"],
  endpoints: (builder) => ({
    fetchContent: builder.query<Content[], string | typeof skipToken>({
      queryFn: async (organizationId: string, { dispatch }) => {
        if (!organizationId) {
          return {
            error: {
              error: {
                status: 400,
                statusText: "Consulta inválida",
              },
            },
          };
        }

        try {
          const collectionRef = collection(db, `organizations/${String(organizationId)}/content`);
          const q = query(collectionRef, orderBy("createdAt", "desc"));
          const querySnapshot = await getDocs(q);

          const items = querySnapshot.docs.reduce((acc, doc: any) => {
            return [...acc, { ...doc.data(), id: doc.id }];
          }, [] as Content[]);

          // get the details of every user that created the content
          const userIds = items.map((item) => item.createdBy);
          const uniqueUserIds = [...new Set(userIds)];
          dispatch(fetchUsers(uniqueUserIds));
          dispatch(addOrganizationContent({ organizationId, content: items }));

          return { data: items };
        } catch (error) {
          console.error(error);
          return {
            error: {
              error: {
                status: 500,
                statusText: "Error al cargar el contenido",
              },
            },
          };
        }
      },
      providesTags: ["Content"],
    }),
    fetchContentByStatus: builder.query<
      { items: Content[]; firstDocId: string | null; lastDocId: string | null; totalCount: number; globalCount: number },
      any
    >({
      queryFn: async (params: { organizationId: string; status?: ContentStatus; pageSize: number; action: "none" | "next" | "previous"; firstDocId?: string | null; lastDocId?: string | null; searchQuery?: string }, { dispatch }) => {
        const { organizationId, status, pageSize, action, searchQuery } = params;

        if (!organizationId) {
          return {
            error: {
              error: {
                status: 500,
                statusText: "Consulta inválida",
              },
            },
          };
        }

        try {
          const collectionRef = collection(db, `organizations/${String(organizationId)}/content`);

          // Fetch status-specific count for pagination UI
          const countConstraints: QueryConstraint[] = [];
          if (searchQuery) {
            countConstraints.push(where("title", ">=", searchQuery));
            countConstraints.push(where("title", "<=", searchQuery + "\uf8ff"));
          }
          if (status) {
            countConstraints.push(where("status", "==", status));
          }

          const countQuery = query(collectionRef, ...countConstraints);
          const countSnapshot = await getCountFromServer(countQuery);
          const totalCount = countSnapshot.data().count;

          // Fetch global count for Quota (all statuses)
          const globalCountQuery = query(collectionRef);
          const globalCountSnapshot = await getCountFromServer(globalCountQuery);
          const globalCount = globalCountSnapshot.data().count;

          const constraints: QueryConstraint[] = [];

          const hasSearch = !!searchQuery;

          if (hasSearch) {
            constraints.push(where("title", ">=", searchQuery));
            constraints.push(where("title", "<=", searchQuery + "\uf8ff"));
          }

          if (status) {
            constraints.push(where("status", "==", status));
          }

          // NONE / DEFAULT — initial load or search reset
          if (action === "none" && !hasSearch) {
            constraints.push(orderBy("createdAt", "desc"));
          }
          if (action === "none" && hasSearch) {
            constraints.push(orderBy("title"));
          }

          // ON NEXT
          if (action === "next" && params.lastDocId) {
            if (hasSearch) {
              constraints.push(orderBy("title"));
            } else {
              constraints.push(orderBy("createdAt", "desc"));
            }
            const cursorRef = doc(db, `organizations/${String(organizationId)}/content/${params.lastDocId}`);
            const cursorSnap = await getDoc(cursorRef);
            if (cursorSnap.exists()) {
              constraints.push(startAfter(cursorSnap));
            }
          }

          // ON PREVIOUS
          if (action === "previous" && params.firstDocId) {
            if (hasSearch) {
              constraints.push(orderBy("title", "desc"));
            } else {
              constraints.push(orderBy("createdAt", "asc"));
            }
            const cursorRef = doc(db, `organizations/${String(organizationId)}/content/${params.firstDocId}`);
            const cursorSnap = await getDoc(cursorRef);
            if (cursorSnap.exists()) {
              constraints.push(startAfter(cursorSnap));
            }
          }

          constraints.push(firestoreLimit(pageSize));

          const q = query(collectionRef, ...constraints);
          const querySnapshot = await getDocs(q);

          let docs = querySnapshot.docs;
          if (action === "previous") {
            docs = [...docs].reverse();
          }

          const items = docs.reduce((acc, doc: any) => {
            return [...acc, { ...doc.data(), id: doc.id }];
          }, [] as Content[]);

          const firstDoc = docs[0];
          const lastDoc = docs[docs.length - 1];

          // get the details of every user that created the content
          const userIds = items.map((item) => item.createdBy);
          const uniqueUserIds = [...new Set(userIds)];
          dispatch(fetchUsers(uniqueUserIds));
          dispatch(addOrganizationContent({ organizationId, content: items }));

          return {
            data: {
              items,
              firstDocId: firstDoc ? firstDoc.id : null,
              lastDocId: lastDoc ? lastDoc.id : null,
              totalCount,
              globalCount,
            },
          };
        } catch (error) {
          console.error(error);
          return {
            error: {
              error: {
                status: 500,
                statusText: "Error al cargar el contenido",
              },
            },
          };
        }
      },
      providesTags: ["Content"],
    }),
    fetchDisabledDishes: builder.query<Content[], { organizationId: string; activeBranch?: string | null }>({
      queryFn: async ({ organizationId, activeBranch }) => {
        const ref = collection(db, `organizations/${String(organizationId)}/content`);
        let items: any[] = [];

        // Explicitly check for the branch string
        if (activeBranch && activeBranch !== "") {
          const q1 = query(ref, where(`branchSettings.${activeBranch}.isDisabled`, "==", true));
          const q2 = query(ref, where("isDisabled", "==", true));
          const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);

          const mergedMap = new Map();
          snap1.docs.forEach(doc => mergedMap.set(doc.id, { ...doc.data(), id: doc.id }));
          snap2.docs.forEach(doc => mergedMap.set(doc.id, { ...doc.data(), id: doc.id }));
          items = Array.from(mergedMap.values());
        } else {
          const q = query(ref, where("isDisabled", "==", true));
          const snap = await getDocs(q);
          items = snap.docs.map(doc => ({ ...doc.data(), id: doc.id }));
        }

        return { data: items };
      },
      providesTags: (result, error, arg) => [
        { type: 'DisabledContent', id: arg.activeBranch || 'GLOBAL' }
      ],
    }),
    addContent: builder.mutation<Content, string | typeof skipToken>({
      queryFn: async ({ draft, organizationId }: { draft: DraftContent; organizationId: string }, { dispatch, getState }) => {
        const TASK = "Crear contenido";

        if (!organizationId || !draft) {
          return {
            error: {
              status: 400,
              statusText: "Consulta inválida",
            },
          };
        }

        const store = getState() as RootState;

        try {
          dispatch(addLoadingTask(TASK));
          const collectionRef = collection(db, `organizations/${String(organizationId)}/content`);

          const ALLOWED_FIELDS = ["title", "previewDescription", "description", "price", "images", "category", "elements", "status", "order", "extras", "schedule", "branchSettings"];

          const payload: Partial<Content> = {
            ...pick(draft, ALLOWED_FIELDS),
            createdBy: store.auth.user.uid,
            createdAt: Date.now(),
          };

          const doc = await addDoc(collectionRef, payload);

          return {
            data: {
              ...payload,
              id: doc.id,
            },
          };
        } catch (error) {
          return {
            error: {
              status: 500,
              statusText: "Error al crear el contenido",
            },
          };
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      invalidatesTags: ["Content"],
    }),
    updateContent: builder.mutation({
      queryFn: async (
        {
          organizationId,
          contentId,
          content,
        }: {
          organizationId: string | typeof skipToken;
          contentId: string;
          content: Content;
        },
        { getState, dispatch }
      ) => {
        const TASK = "Actualizando contenido";

        const ALLOWED_FIELDS = [
          "title",
          "description",
          "previewDescription",
          "price",
          "images",
          "category",
          "status",
          "quantity",
          "available",
          "order",
          "elements",
          "startingPrice",
          "isDisabled",
          "extras",
          "schedule",
          "branchSettings",
          "loyalty",
        ];

        try {
          dispatch(addLoadingTask(TASK));

          const store = getState() as RootState;

          if (!store.auth?.user?.uid || !organizationId || !contentId) {
            throw new Error("Invalid payload");
          }
          const docRef = doc(db, `organizations/${String(organizationId)}/content/${String(contentId)}`);

          const payload = {
            ...pick(content, ALLOWED_FIELDS),
            lastUpdatedBy: store.auth.user.uid,
            lastUpdatedAt: Date.now(),
          };

          const response = await updateDoc(docRef, payload);
          return { data: response };
        } catch (error) {
          console.error(error);
          throw error;
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      invalidatesTags: ["Content"],
    }),
    enableContent: builder.mutation({
      queryFn: async ({ organizationId, contentId, activeBranch }, { getState }) => {
        const store = getState() as RootState;
        const docRef = doc(db, `organizations/${String(organizationId)}/content/${String(contentId)}`);

        const updatePath = activeBranch ? `branchSettings.${activeBranch}.isDisabled` : "isDisabled";

        await updateDoc(docRef, {
          [updatePath]: false,
          lastUpdatedBy: store.auth.user.uid,
          lastUpdatedAt: Date.now(),
        });

        return { data: { id: contentId } };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'DisabledContent', id: arg.activeBranch || 'GLOBAL' },
        "Content"
      ],
    }),
    addOffer: builder.mutation({
      queryFn: async ({ draft, organizationId }: { draft: NewOffer; organizationId: string }, { dispatch, getState }) => {
        const TASK = "Crear Oferta";
        const store = getState() as RootState;

        try {
          dispatch(addLoadingTask(TASK));
          const collectionRef = collection(db, `organizations/${String(organizationId)}/content`);

          const ALLOWED_FIELDS = ["createdAt", "createdBy", "title", "images", "status", "type", "region", "component", "schedule"];

          const payload: NewOffer = {
            ...pick(draft, ALLOWED_FIELDS),
            type: ContentType.OFFER,
            createdBy: store.auth.user.uid,
            createdAt: Date.now(),
          };

          const doc = await addDoc(collectionRef, payload);
          return { data: doc as unknown as NewOffer };
        } catch (error) {
          console.error(error);
          throw error;
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      invalidatesTags: ["Content"],
    }),
    updateOffer: builder.mutation({
      queryFn: async (
        {
          organizationId,
          contentId,
          content,
        }: {
          organizationId: string | typeof skipToken;
          contentId: string;
          content: Offer;
        },
        { getState, dispatch }
      ) => {
        const TASK = "Actualizando Oferta";
        const ALLOWED_FIELDS = ["title", "images", "status", "type", "lastUpdatedBy", "lastUpdatedAt", "region", "component", "schedule"];

        try {
          dispatch(addLoadingTask(TASK));

          const { auth } = getState() as RootState;

          if (!auth?.user?.uid || !organizationId || !contentId) {
            throw new Error("Invalid payload");
          }
          const docRef = doc(db, `organizations/${String(organizationId)}/content/${String(contentId)}`);

          const payload: Offer = {
            ...pick(content, ALLOWED_FIELDS),
            lastUpdatedBy: auth.user.uid,
            lastUpdatedAt: Date.now(),
          };

          const response = await updateDoc(docRef, payload as any);
          return { data: response };
        } catch (error) {
          console.error(error);
          throw error;
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      invalidatesTags: ["Content"],
    }),
    deleteContent: builder.mutation({
      queryFn: async (
        {
          organizationId,
          contentId,
        }: {
          organizationId: string | typeof skipToken;
          contentId: string;
        },
        { getState }
      ) => {
        try {
          const { auth } = getState() as RootState;
          if (!auth?.user?.uid || !organizationId || !contentId) {
            throw new Error("Invalid payload");
          }
          const docRef = doc(db, `organizations/${String(organizationId)}/content/${String(contentId)}`);
          await deleteDoc(docRef);
          return { data: true };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: ["Content"],
    }),
    duplicateContent: builder.mutation({
      queryFn: async (
        {
          organizationId,
          contentId,
        }: {
          organizationId: string | typeof skipToken;
          contentId: string;
        },
        { getState }
      ) => {
        try {

          const { auth } = getState() as RootState;

          if (!auth?.user?.uid || !organizationId || !contentId) {
            throw new Error("Invalid payload");
          }

          const docRef = doc(db, `organizations/${String(organizationId)}/content/${String(contentId)}`);
          const docSnap = await getDoc(docRef);

          if (!docSnap.exists()) {
            throw new Error("Content not found");
          }

          const originalData = docSnap.data() as Content;
          const collectionRef = collection(db, `organizations/${String(organizationId)}/content`);

          const payload = {
            ...originalData,
            title: `${originalData.title} (Copia)`,
            createdBy: auth.user.uid,
            createdAt: Date.now(),
          };

          // Remove the ID from the payload to allow Firestore to generate a new one
          delete (payload as any).id;
          delete payload.lastUpdatedAt;
          delete payload.lastUpdatedBy;

          const newDoc = await addDoc(collectionRef, payload);

          return {
            data: {
              ...payload,
              id: newDoc.id,
            },
          };
        } catch (error) {
          console.error(error);
          return { error };
        }
      },
      invalidatesTags: ["Content"],
    }),
  }),
});

export const {
  useFetchContentQuery,
  useLazyFetchContentQuery,
  useFetchContentByStatusQuery,
  useLazyFetchContentByStatusQuery,
  useFetchDisabledDishesQuery,
  useAddContentMutation,
  useUpdateContentMutation,
  useAddOfferMutation,
  useUpdateOfferMutation,
  useDeleteContentMutation,
  useEnableContentMutation,
  useDuplicateContentMutation,
} = contentApi;
